import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../partage/api/client';
import type { BulleDeFeuille, Feuille } from '../partage/api/types';
import { useCadreDuHaut } from '../partage/cadreDuHaut';
import { gestesDuToucher } from '../partage/deuxGestes';
import { IconeDossierDuCarnet, IconeFichierDuCarnet } from '../partage/icones';
import { cles } from '../partage/relectures';
import { useFeuilles } from '../partage/useArbre';
import { REGLAGES_PAR_DEFAUT, requeteDesReglages } from '../maintenant';
import {
  CE_QUI_PRESSE,
  DEPLIER_SES_TACHES,
  faitesSurTotal,
  REPLIER_SES_TACHES,
  TOURNE_TON_TELEPHONE,
  UNE_BRANCHE,
  uneBulleDans,
} from '../partage/vocabulaire';
import { attendLeMonde, basculerLaBranche, ceQueDitLeBout, deplieesDuProjet, lignesDeCeQuiPresse, montreesDansLArbre } from './arbreAuPouce';
import { rangerDansMonOrdre } from './rangerLaListe';
import './ArbreDuProjet.css';

/**
 * Un projet ouvert au téléphone, en arborescence (chantier telephone-projet, 02, maquette
 * `app-au-telephone.html`, `projet()`, la branche de l'arbre, `.arbre`, `.nd`, `.n0`, `.n1`,
 * `.n2`, `.sous`, `.fl`, `.tx`, `.nb`, `.ajout`, `icD`, `icF`, `.postit`). Un morceau à part :
 * l'ordinateur garde sa Liste, sa Carte et sa Hiérarchie.
 *
 * En haut, le projet (icône dossier, titre à la main, « faites/total ») ; dessous, ses enfants
 * dans ton ordre. Un enfant qui contient quelque chose est un dossier : ▸ le déplie sur place,
 * son nom ouvre son détail (`onEntrer`) ; dépliés, ses enfants sont dessous, avec un trait. Un
 * enfant au bout est un fichier : son nom ouvre sa fiche en page (sa feuille, 04), l'appui long la
 * fiche qui monte (`onFiche`) ; sa réponse attendue ou son échéance à droite. Un + à chaque niveau ouvre le + de la barre du bas, déjà dans la bonne bulle.
 * Sous l'arbre, le post-it « Ce qui presse », puis l'encadré « Tourne ton téléphone » (chantier
 * telephone-couche, 01, maquette `.tourne-ind`) : couché, l'Analyse et la Hiérarchie du projet.
 *
 * Les comptes viennent du serveur (`decompte`) ; une branche dépliée lit sa feuille, comme la
 * Liste de l'ordinateur. Ce qui est déplié tient le temps de la session (`arbreAuPouce.ts`).
 */
export function ArbreDuProjet({
  donnees,
  montrerCeQuiEstFait,
  onEntrer,
  onFiche,
}: {
  donnees: Feuille;
  montrerCeQuiEstFait: boolean;
  onEntrer: (id: string) => void;
  onFiche: (id: string) => void;
}) {
  const { bulle, enfants, ordreHierarchique } = donnees;
  const cadre = useCadreDuHaut();
  const [depliees, setDepliees] = useState(() => deplieesDuProjet(bulle.id));

  const branches = montreesDansLArbre(rangerDansMonOrdre(enfants, ordreHierarchique), montrerCeQuiEstFait);
  const aLire = branches.filter((b) => b.nbEnfants > 0 && depliees.has(b.id)).map((b) => b.id);
  const lues = useFeuilles(aLire);
  const feuilleDe = new Map(aLire.map((id, i) => [id, lues[i]?.data]));

  const ajouterDans = (id: string) => cadre?.ouvrirLePlusDans?.(id);

  return (
    <>
      <div className="arbre-projet">
        <div className="arbre-nd arbre-nd--n0">
          <span className="arbre-tx">
            <IconeDossier />
            {bulle.titre}
          </span>
          <span className="arbre-nb">{faitesSurTotal(bulle.decompte.faites, bulle.decompte.total)}</span>
        </div>
        <div className="arbre-sous arbre-sous--s0">
          {branches.map((branche) => {
            if (branche.nbEnfants === 0) {
              return <Fichier key={branche.id} bulle={branche} niveau="n1" onPage={onEntrer} onFiche={onFiche} />;
            }
            const ouverte = depliees.has(branche.id);
            const sienne = feuilleDe.get(branche.id);
            const dedans = sienne
              ? montreesDansLArbre(rangerDansMonOrdre(sienne.enfants, sienne.ordreHierarchique), montrerCeQuiEstFait)
              : [];
            return (
              <div key={branche.id} className="arbre-branche">
                <div
                  className={`arbre-nd arbre-nd--n1${attendLeMonde(branche) ? ' est-en-attente' : ''}${branche.etat === 'fait' ? ' est-fait' : ''}`}
                  data-tuto="bulle-liste"
                  data-bulle={branche.id}
                >
                  <button
                    type="button"
                    className="arbre-fl"
                    aria-expanded={ouverte}
                    aria-label={ouverte ? REPLIER_SES_TACHES : DEPLIER_SES_TACHES}
                    onClick={() => setDepliees(basculerLaBranche(bulle.id, branche.id))}
                  >
                    {ouverte ? '▾' : '▸'}
                  </button>
                  <button type="button" className="arbre-tx" onClick={() => onEntrer(branche.id)}>
                    <IconeDossier />
                    {branche.titre}
                  </button>
                  <span className="arbre-nb">{faitesSurTotal(branche.decompte.faites, branche.decompte.total)}</span>
                </div>
                {ouverte && (
                  <div className="arbre-sous">
                    {dedans.map((petite) =>
                      petite.nbEnfants > 0 ? (
                        <div
                          key={petite.id}
                          className={`arbre-nd arbre-nd--n2${attendLeMonde(petite) ? ' est-en-attente' : ''}${petite.etat === 'fait' ? ' est-fait' : ''}`}
                          data-tuto="bulle-liste"
                          data-bulle={petite.id}
                        >
                          <button type="button" className="arbre-tx" onClick={() => onEntrer(petite.id)}>
                            <IconeDossier />
                            {petite.titre}
                          </button>
                          <span className="arbre-nb">{faitesSurTotal(petite.decompte.faites, petite.decompte.total)}</span>
                        </div>
                      ) : (
                        <Fichier key={petite.id} bulle={petite} niveau="n2" onPage={onEntrer} onFiche={onFiche} />
                      )
                    )}
                    <button type="button" className="arbre-nd arbre-nd--n2 arbre-ajout" onClick={() => ajouterDans(branche.id)}>
                      {uneBulleDans(branche.titre)}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          <button type="button" className="arbre-nd arbre-nd--n1 arbre-ajout" onClick={() => ajouterDans(bulle.id)}>
            {UNE_BRANCHE}
          </button>
        </div>
      </div>

      <CeQuiPresse projet={bulle.id} />

      <p className="arbre-tourne">
        <span className="arbre-tourne-ico" aria-hidden="true">
          ↻
        </span>
        <span>{TOURNE_TON_TELEPHONE}</span>
      </p>
    </>
  );
}

/**
 * Un fichier : une bulle au bout. Son nom ouvre sa fiche en page, à sa propre adresse (04) ; le
 * doigt gardé posé, la fiche qui monte, sur place.
 */
function Fichier({
  bulle,
  niveau,
  onPage,
  onFiche,
}: {
  bulle: BulleDeFeuille;
  niveau: 'n1' | 'n2';
  onPage: (id: string) => void;
  onFiche: (id: string) => void;
}) {
  const bout = ceQueDitLeBout(bulle);
  return (
    <div
      className={`arbre-nd arbre-nd--${niveau} arbre-nd--fichier${attendLeMonde(bulle) ? ' est-en-attente' : ''}${bulle.etat === 'fait' ? ' est-fait' : ''}`}
      data-tuto="bulle-liste"
      data-bulle={bulle.id}
    >
      {niveau === 'n1' && <span className="arbre-fl" aria-hidden="true" />}
      <button type="button" className="arbre-tx" {...gestesDuToucher(() => onPage(bulle.id), () => onFiche(bulle.id))}>
        <IconeFichier />
        {bulle.titre}
      </button>
      {bout && <span className="arbre-nb">{bout}</span>}
    </div>
  );
}

/**
 * Le post-it « Ce qui presse », sous l'arbre : ce que le serveur range en Prioritaire pour ce
 * projet, puis ce qui attend le monde. Rien : pas de post-it.
 */
function CeQuiPresse({ projet }: { projet: string }) {
  const requete = requeteDesReglages(REGLAGES_PAR_DEFAUT, projet, false);
  const vivier = useQuery({ queryKey: cles.ceQuiPresse(projet, requete), queryFn: () => api.maintenant(requete) });
  const lignes = vivier.data ? lignesDeCeQuiPresse(vivier.data) : [];
  if (lignes.length === 0) return null;
  return (
    <div className="arbre-postit">
      <span className="arbre-postit-punaise" aria-hidden="true" />
      <h2 className="arbre-postit-titre">{CE_QUI_PRESSE}</h2>
      {lignes.map((l) => (
        <p key={l.id} className={`arbre-postit-ligne${l.monde ? ' arbre-postit-ligne--monde' : ''}`}>
          {l.texte}
        </p>
      ))}
    </div>
  );
}

/** Le dossier (`icD`) et le fichier (`icF`), à la taille de l'arbre. */
export function IconeDossier() {
  return <IconeDossierDuCarnet className="arbre-io" />;
}

function IconeFichier() {
  return <IconeFichierDuCarnet className="arbre-io" />;
}
